import { createFileRoute } from "@tanstack/react-router";
import debounce from "common/utils/debounce";
import AdminPage from "lib/components/admin/builders/AdminPage";
import styles from "lib/components/admin/selections/AdminSelections.module.scss";
import AdminSelectionsAddModal, { isAddScenario } from "lib/components/admin/selections/AdminSelectionsAddModal";
import AdminSelectionsEditBrochureDataModal from "lib/components/admin/selections/AdminSelectionsEditBrochureDataModal";
import AdminSelectionsEditManifestModal from "lib/components/admin/selections/AdminSelectionsEditManifestModal";
import AdminSelectionsEditModal, { isEditScenario } from "lib/components/admin/selections/AdminSelectionsEditModal";
import AdminSelectionsEnvironmentsDetail from "lib/components/admin/selections/AdminSelectionsEnvironmentsDetail";
import AdminSelectionsEnvironmentsList from "lib/components/admin/selections/AdminSelectionsEnvironmentsList";
import AdminSelectionsImportManifestModal from "lib/components/admin/selections/AdminSelectionsImportManifestModal";
import AdminSelectionsPublishEnvironmentModal from "lib/components/admin/selections/AdminSelectionsPublishEnvironmentModal";
import adminSelectionsReducer, {
  AdminSelectionsActionType,
} from "lib/components/admin/selections/adminSelectionsReducer";
import { getSelectionsDatabase, saveSelectionsDatabase } from "lib/storage/selectionsStorage";
import { useEffect, useReducer, useRef } from "react";
import Button from "ui/buttons/Button";
import Loading from "ui/components/Loading";
import useAppContext from "ui/hooks/useAppContext";
import IconFileOpen from "ui/icons/IconFileOpen";
import SplitFlex from "ui/layouts/SplitFlex";
import withAuthentication from "../../components/withAuthentication";

const SAVE_DATABASE_DEBOUNCE_INTERVAL_MS = 2000;

function AdminSelectionsPage() {
  const { setUserNotification } = useAppContext();

  const [state, dispatch] = useReducer(adminSelectionsReducer, { loading: true });

  useEffect(() => {
    async function fetchSelectionDatabase() {
      const database = await getSelectionsDatabase();
      if (database) {
        dispatch({
          type: AdminSelectionsActionType.LOAD_DATABASE,
          database,
        });
      }
    }
    void fetchSelectionDatabase();
  }, []);

  useEffect(() => {
    if (state.databaseUnsaved) {
      debouncedSaveDatabase.current(state.database);
    }
  }, [state.databaseUnsaved]);

  const debouncedSaveDatabase = useRef(
    debounce(async (database) => {
      const saveSuccessful = await saveSelectionsDatabase(database);
      if (!saveSuccessful) {
        setUserNotification({
          message: "Could not save database",
          messageType: "error",
        });
      }
      dispatch({ type: AdminSelectionsActionType.SAVE_DATABASE, status: "done" });
    }, SAVE_DATABASE_DEBOUNCE_INTERVAL_MS),
  );

  const onClose = () => dispatch({ type: AdminSelectionsActionType.SET_MODAL_SCENARIO, scenario: null });

  return (
    <AdminPage currentPage="selections" pageTitle="Admin - Selections">
      <div className={styles.header}>
        <Button
          btnStyle="primary"
          btnSize="medium"
          onClick={() => dispatch({ type: AdminSelectionsActionType.SET_MODAL_SCENARIO, scenario: "database" })}
        >
          <IconFileOpen />
          <div>Edit database</div>
        </Button>
      </div>
      {state.database && (
        <>
          <SplitFlex sizeRatio={0.33} firstMinSize={280} lastMinSize={320}>
            <AdminSelectionsEnvironmentsList state={state} dispatch={dispatch} />
            <AdminSelectionsEnvironmentsDetail state={state} dispatch={dispatch} />
          </SplitFlex>
          {isAddScenario(state.scenario) && (
            <AdminSelectionsAddModal onClose={onClose} state={state} dispatch={dispatch} />
          )}
          {isEditScenario(state.scenario) && (
            <AdminSelectionsEditModal onClose={onClose} state={state} dispatch={dispatch} />
          )}
          {state.scenario === "importManifest" && (
            <AdminSelectionsImportManifestModal onClose={onClose} state={state} />
          )}
          {state.scenario === "editManifest" && <AdminSelectionsEditManifestModal onClose={onClose} state={state} />}
          {state.scenario === "editBrochureData" && (
            <AdminSelectionsEditBrochureDataModal onClose={onClose} state={state} />
          )}
          {state.scenario === "publishEnvironment" && (
            <AdminSelectionsPublishEnvironmentModal onClose={onClose} state={state} dispatch={dispatch} />
          )}
        </>
      )}
      {!state.database && (state.loading ? <Loading /> : <div>Missing selections database</div>)}
    </AdminPage>
  );
}

export const Route = createFileRoute("/admin/selections")({
  component: withAuthentication(AdminSelectionsPage, { superAdmin: true }),
});
